import React, { useState, useEffect, useCallback } from 'react';
import { useAppStore, useAnnotationState } from '@/stores/appStore';
import { AnnotationType } from '@/services/annotationService';
import { useAnnotations } from '@/hooks/useAnnotations';
import { logger } from '@/lib/logger';

interface Point {
  x: number;
  y: number;
}

/**
 * Constrains a line to 45-degree increments (horizontal, vertical, or diagonal)
 * Used when Shift key is held during line/arrow drawing
 */
function constrainToAngles(start: Point, end: Point): Point {
  const dx = end.x - start.x;
  const dy = end.y - start.y;
  const distance = Math.sqrt(dx * dx + dy * dy);

  // Calculate angle in degrees
  const angle = Math.atan2(dy, dx) * (180 / Math.PI);

  // Snap to nearest 45-degree increment
  const snapAngles = [0, 45, 90, 135, 180, -135, -90, -45];
  let closestAngle = 0;
  let minDiff = 360;

  for (const snapAngle of snapAngles) {
    let diff = Math.abs(angle - snapAngle);
    // Handle wrap-around for 180 and -180
    if (diff > 180) diff = 360 - diff;
    if (diff < minDiff) {
      minDiff = diff;
      closestAngle = snapAngle;
    }
  }

  // Convert back to radians and calculate new end point
  const radians = closestAngle * (Math.PI / 180);
  return {
    x: start.x + Math.cos(radians) * distance,
    y: start.y + Math.sin(radians) * distance,
  };
}

interface AnnotationDrawingOverlayProps {
  floorPlanId: string;
  containerRef: React.RefObject<HTMLDivElement>;
  onComplete?: () => void;
  zoom: number;
  position: { x: number; y: number };
  imageSize: { width: number; height: number };
}

export function AnnotationDrawingOverlay({
  floorPlanId,
  containerRef,
  onComplete,
  zoom,
  position,
  imageSize,
}: AnnotationDrawingOverlayProps) {
  const [isDrawing, setIsDrawing] = useState(false);
  const [startPoint, setStartPoint] = useState<Point | null>(null);
  const [currentPoint, setCurrentPoint] = useState<Point | null>(null);
  const [previewText, setPreviewText] = useState('');
  const [isEditingText, setIsEditingText] = useState(false);

  // Get annotation functions from hook
  const { createAnnotation: createAnnotationMutation } = useAnnotations(floorPlanId);

  // Get annotation state from store
  const { activeAnnotationType, isAnnotating, currentStyle } = useAnnotationState();
  const setIsAnnotating = useAppStore(state => state.setIsAnnotating);
  const setActiveAnnotationType = useAppStore(state => state.setActiveAnnotationType);
  const isEditingTextGlobal = useAppStore(state => state.annotations.isEditingText);
  const setIsEditingTextGlobal = useAppStore(state => state.setIsEditingText);
  const selectEntity = useAppStore(state => state.selectEntity);

  // Convert screen coordinates to floor plan coordinates (matching RoomCreationOverlay)
  const screenToFloorPlanCoords = useCallback(
    (clientX: number, clientY: number): Point | null => {
      if (!containerRef.current) return null;

      const rect = containerRef.current.getBoundingClientRect();
      // Calculate position relative to the viewport
      const x = (clientX - rect.left - position.x) / zoom;
      const y = (clientY - rect.top - position.y) / zoom;

      return { x, y };
    },
    [containerRef, position, zoom],
  );

  // Handle mouse down
  const handleMouseDown = useCallback(
    (e: MouseEvent) => {
      if (!isAnnotating || !activeAnnotationType) return;

      // Don't create while locally editing or if global text editing is active
      if (isEditingText || isEditingTextGlobal) return;

      // Check if we're clicking on a text annotation that's being edited
      const target = e.target as HTMLElement;
      if (target.tagName === 'TEXTAREA' || target.closest('foreignObject')) {
        return; // Don't create new annotation when clicking on text editor
      }

      // Check if we're clicking on any interactive element
      const isInteractiveElement =
        target.closest('[data-fixture-id]') ||
        target.closest('[data-control-id]') ||
        target.closest('[data-panel-id]') ||
        target.closest('[data-room-id]') ||
        target.closest('[data-product-id]') ||
        target.closest('[data-annotation-id]') ||
        target.closest('[data-rotation-handle]') ||
        target.closest('[data-fixture-badge-id]') ||
        target.closest('[data-control-badge-id]');

      if (isInteractiveElement) {
        // Exit annotation mode and let the click through to select the element
        setActiveAnnotationType(null);
        return; // Don't prevent default - let the element handle the click
      }

      const point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      logger.log(
        'AnnotationDrawingOverlay - Mouse down at:',
        point,
        'zoom:',
        zoom,
        'position:',
        position,
        'imageSize:',
        imageSize,
      );

      setStartPoint(point);
      setCurrentPoint(point);
      setIsDrawing(true);

      e.preventDefault();
      e.stopPropagation();
    },
    [
      isAnnotating,
      activeAnnotationType,
      screenToFloorPlanCoords,
      zoom,
      position,
      isEditingTextGlobal,
      isEditingText,
      setActiveAnnotationType,
    ],
  );

  // Handle mouse move
  const handleMouseMove = useCallback(
    (e: MouseEvent) => {
      if (!isDrawing || !startPoint) return;

      const point = screenToFloorPlanCoords(e.clientX, e.clientY);
      if (!point) return;

      // Apply Shift key constraint for line-based annotations (line, arrow)
      const isLineBased = activeAnnotationType === 'line' || activeAnnotationType === 'arrow';
      if (e.shiftKey && isLineBased) {
        const constrainedPoint = constrainToAngles(startPoint, point);
        setCurrentPoint(constrainedPoint);
      } else {
        setCurrentPoint(point);
      }
    },
    [isDrawing, startPoint, screenToFloorPlanCoords, activeAnnotationType],
  );

  // Create annotation based on type. Click-or-drag aware: a near-zero-distance
  // mouseup creates a default-sized shape anchored at the click point;
  // a real drag uses start→current as the bounding rect.
  const createAnnotation = useCallback(async () => {
    if (!startPoint || !currentPoint || !activeAnnotationType) return;

    // Click-or-drag detection in floor-plan coords. ~5 screen px tolerance.
    const dxScreen = currentPoint.x - startPoint.x;
    const dyScreen = currentPoint.y - startPoint.y;
    const distance = Math.sqrt(dxScreen * dxScreen + dyScreen * dyScreen);
    const isClickOnly = distance < 5 / zoom;

    // Default sizes for click-only placement, in floor-plan coords.
    const DEFAULT_RECT_W = 80;
    const DEFAULT_RECT_H = 60;
    const DEFAULT_CIRCLE_R = 25;
    const DEFAULT_LINE_LEN = 100;
    const DEFAULT_TEXT_W = 120;
    const DEFAULT_TEXT_H = 50;

    let annotationData;

    switch (activeAnnotationType) {
      case 'line':
        annotationData = {
          floor_plan_id: floorPlanId,
          type: 'line' as AnnotationType,
          data: isClickOnly
            ? {
                start: startPoint,
                end: { x: startPoint.x + DEFAULT_LINE_LEN, y: startPoint.y },
              }
            : { start: startPoint, end: currentPoint },
          style: currentStyle,
        };
        break;

      case 'rectangle': {
        if (isClickOnly) {
          annotationData = {
            floor_plan_id: floorPlanId,
            type: 'rectangle' as AnnotationType,
            data: {
              x: startPoint.x - DEFAULT_RECT_W / 2,
              y: startPoint.y - DEFAULT_RECT_H / 2,
              width: DEFAULT_RECT_W,
              height: DEFAULT_RECT_H,
            },
            style: currentStyle,
          };
        } else {
          const width = Math.abs(currentPoint.x - startPoint.x);
          const height = Math.abs(currentPoint.y - startPoint.y);
          const x = Math.min(startPoint.x, currentPoint.x);
          const y = Math.min(startPoint.y, currentPoint.y);
          annotationData = {
            floor_plan_id: floorPlanId,
            type: 'rectangle' as AnnotationType,
            data: { x, y, width, height },
            style: currentStyle,
          };
        }
        break;
      }

      case 'circle': {
        if (isClickOnly) {
          annotationData = {
            floor_plan_id: floorPlanId,
            type: 'circle' as AnnotationType,
            data: {
              cx: startPoint.x,
              cy: startPoint.y,
              radius: DEFAULT_CIRCLE_R,
            },
            style: currentStyle,
          };
        } else {
          // Calculate circle with fixed starting point
          const circleDx = currentPoint.x - startPoint.x;
          const circleDy = currentPoint.y - startPoint.y;
          // Use the smaller dimension to keep it circular
          const circleDiameter = Math.min(Math.abs(circleDx), Math.abs(circleDy));
          // Determine direction and calculate center
          const circleCx = startPoint.x + (circleDx > 0 ? circleDiameter / 2 : -circleDiameter / 2);
          const circleCy = startPoint.y + (circleDy > 0 ? circleDiameter / 2 : -circleDiameter / 2);

          annotationData = {
            floor_plan_id: floorPlanId,
            type: 'circle' as AnnotationType,
            data: {
              cx: circleCx,
              cy: circleCy,
              radius: circleDiameter / 2,
            },
            style: currentStyle,
          };
        }
        break;
      }

      case 'arrow':
        annotationData = {
          floor_plan_id: floorPlanId,
          type: 'arrow' as AnnotationType,
          data: isClickOnly
            ? {
                start: startPoint,
                end: { x: startPoint.x + DEFAULT_LINE_LEN, y: startPoint.y },
                label: previewText,
              }
            : { start: startPoint, end: currentPoint, label: previewText },
          style: currentStyle,
        };
        break;

      case 'text': {
        // For text, create a rectangle-like area. Click-only places a default
        // box at the click point; drag uses the dragged bounds.
        if (isClickOnly) {
          annotationData = {
            floor_plan_id: floorPlanId,
            type: 'text' as AnnotationType,
            data: {
              x: startPoint.x,
              y: startPoint.y,
              width: DEFAULT_TEXT_W,
              height: DEFAULT_TEXT_H,
              text: '',
            },
            style: currentStyle,
          };
        } else {
          const textWidth = Math.abs(currentPoint.x - startPoint.x);
          const textHeight = Math.abs(currentPoint.y - startPoint.y);
          const textX = Math.min(startPoint.x, currentPoint.x);
          const textY = Math.min(startPoint.y, currentPoint.y);

          annotationData = {
            floor_plan_id: floorPlanId,
            type: 'text' as AnnotationType,
            data: {
              x: textX,
              y: textY,
              width: Math.max(textWidth, DEFAULT_TEXT_W),
              height: Math.max(textHeight, DEFAULT_TEXT_H),
              text: '',
            },
            style: currentStyle,
          };
        }
        break;
      }

      default:
        return;
    }

    logger.log('Creating annotation with data:', annotationData, { isClickOnly, distance });

    // Create annotation with optimistic update
    const newAnnotation = await createAnnotationMutation(annotationData);

    // For text annotations, return the created annotation so the caller can
    // trigger edit mode. Tool deselect + auto-select still happen in handleMouseUp
    // after edit mode is dismissed.
    if (activeAnnotationType === 'text' && newAnnotation) {
      return newAnnotation;
    }

    // Delay clearing points to ensure smooth transition
    setTimeout(() => {
      setStartPoint(null);
      setCurrentPoint(null);
      setPreviewText('');
    }, 50); // Small delay to ensure optimistic update is applied

    // Auto-deselect the tool so the user is back in select mode after one placement.
    setActiveAnnotationType(null);

    // Auto-select the new annotation so its handles appear and the contextual
    // toolbar pops up immediately for size/style adjustment.
    if (newAnnotation?.id) {
      selectEntity('annotation', newAnnotation.id);
    }

    // Notify parent component
    if (onComplete) {
      onComplete();
    }

    return newAnnotation;
  }, [
    startPoint,
    currentPoint,
    activeAnnotationType,
    floorPlanId,
    currentStyle,
    previewText,
    onComplete,
    createAnnotationMutation,
    setActiveAnnotationType,
    selectEntity,
    zoom,
  ]);

  // Handle mouse up
  const handleMouseUp = useCallback(
    async (e: MouseEvent) => {
      if (!isDrawing) return;

      // Reset drawing state immediately to allow new drawing
      setIsDrawing(false);

      // Create annotation in the background
      const newAnnotation = await createAnnotation();

      // For text annotations, we need to trigger edit mode.
      // Defer the dispatch by one frame so React has time to mount the new
      // TextAnnotation component (the listener for `startTextEdit` is added
      // in its useEffect). Without the defer, the event fires while the
      // optimistic-update render is still pending and the listener doesn't
      // exist yet, so the text box appears but never enters edit mode.
      if (activeAnnotationType === 'text' && newAnnotation) {
        setIsEditingText(true);
        const idToEdit = newAnnotation.id;
        requestAnimationFrame(() => {
          window.dispatchEvent(
            new CustomEvent('startTextEdit', { detail: { annotationId: idToEdit } }),
          );
        });
      }
    },
    [isDrawing, createAnnotation, activeAnnotationType],
  );

  // Touch event handlers
  const handleTouchStart = useCallback(
    (e: TouchEvent) => {
      if (!isAnnotating || !activeAnnotationType || e.touches.length !== 1) return;

      // Don't create while editing text
      if (isEditingText || isEditingTextGlobal) return;

      const touch = e.touches[0];
      const point = screenToFloorPlanCoords(touch.clientX, touch.clientY);
      if (!point) return;

      logger.log(
        'AnnotationDrawingOverlay - Touch start at:',
        point,
        'zoom:',
        zoom,
        'position:',
        position,
        'imageSize:',
        imageSize,
      );

      setStartPoint(point);
      setCurrentPoint(point);
      setIsDrawing(true);

      e.preventDefault();
      e.stopPropagation();
    },
    [
      isAnnotating,
      activeAnnotationType,
      screenToFloorPlanCoords,
      zoom,
      position,
      isEditingText,
      isEditingTextGlobal,
    ],
  );

  const handleTouchMove = useCallback(
    (e: TouchEvent) => {
      if (!isDrawing || !startPoint || e.touches.length !== 1) return;

      const touch = e.touches[0];
      const point = screenToFloorPlanCoords(touch.clientX, touch.clientY);
      if (!point) return;

      setCurrentPoint(point);
      e.preventDefault();
    },
    [isDrawing, startPoint, screenToFloorPlanCoords],
  );

  const handleTouchEnd = useCallback(
    async (e: TouchEvent) => {
      if (!isDrawing) return;

      // Reset drawing state immediately to allow new drawing
      setIsDrawing(false);

      // Create annotation in the background
      const newAnnotation = await createAnnotation();

      // For text annotations, we need to trigger edit mode.
      // Defer the dispatch by one frame so React has time to mount the new
      // TextAnnotation component (the listener for `startTextEdit` is added
      // in its useEffect). Without the defer, the event fires while the
      // optimistic-update render is still pending and the listener doesn't
      // exist yet, so the text box appears but never enters edit mode.
      if (activeAnnotationType === 'text' && newAnnotation) {
        setIsEditingText(true);
        const idToEdit = newAnnotation.id;
        requestAnimationFrame(() => {
          window.dispatchEvent(
            new CustomEvent('startTextEdit', { detail: { annotationId: idToEdit } }),
          );
        });
      }

      e.preventDefault();
    },
    [isDrawing, createAnnotation, activeAnnotationType],
  );

  // Handle ESC key and text edit complete
  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsDrawing(false);
        setStartPoint(null);
        setCurrentPoint(null);
        setPreviewText('');
        setActiveAnnotationType(null);
      }
    };

    const handleTextEditComplete = (e: CustomEvent) => {
      // When text editing is complete
      setIsEditingText(false);

      // Apply the same auto-deselect / auto-select pattern that shape
      // creation uses: once the user finishes editing the new text (clicks
      // outside or hits Escape), turn the text tool off and select the new
      // annotation so its handles + contextual toolbar appear immediately.
      // Skip when the annotation was cancelled empty (then it's been
      // deleted; nothing to select).
      const detail = (e as CustomEvent).detail || {};
      if (!detail.wasEmpty && detail.annotationId) {
        setActiveAnnotationType(null);
        selectEntity('annotation', detail.annotationId);
      } else if (detail.wasEmpty) {
        setActiveAnnotationType(null);
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    window.addEventListener('textEditComplete', handleTextEditComplete as EventListener);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
      window.removeEventListener('textEditComplete', handleTextEditComplete as EventListener);
    };
  }, [setActiveAnnotationType, activeAnnotationType, selectEntity]);

  // Add event listeners
  useEffect(() => {
    if (!isAnnotating || !containerRef.current) return;

    const container = containerRef.current;

    // Add a click handler to save text before creating new annotation
    const handleClick = (e: MouseEvent) => {
      // If text is being edited globally, blur any active textarea first
      if (isEditingTextGlobal && activeAnnotationType === 'text') {
        const activeTextarea = document.querySelector('textarea:focus') as HTMLTextAreaElement;
        if (activeTextarea) {
          activeTextarea.blur(); // This will trigger save
          // Prevent immediate new annotation creation
          e.preventDefault();
          e.stopPropagation();
          // Allow next click to create new annotation
          setTimeout(() => setIsEditingText(false), 100);
        }
      }
    };

    // Add event listeners for drawing
    container.addEventListener('click', handleClick, true); // Use capture phase to handle before other events
    container.addEventListener('mousedown', handleMouseDown, true); // Use capture phase to check for interactive elements first
    window.addEventListener('mousemove', handleMouseMove);
    window.addEventListener('mouseup', handleMouseUp);

    // Add touch event listeners
    container.addEventListener('touchstart', handleTouchStart, { passive: false });
    window.addEventListener('touchmove', handleTouchMove, { passive: false });
    window.addEventListener('touchend', handleTouchEnd, { passive: false });

    return () => {
      container.removeEventListener('click', handleClick, true);
      container.removeEventListener('mousedown', handleMouseDown, true); // Also remove with capture phase
      window.removeEventListener('mousemove', handleMouseMove);
      window.removeEventListener('mouseup', handleMouseUp);

      container.removeEventListener('touchstart', handleTouchStart);
      window.removeEventListener('touchmove', handleTouchMove);
      window.removeEventListener('touchend', handleTouchEnd);
    };
  }, [
    isAnnotating,
    activeAnnotationType,
    containerRef,
    handleMouseDown,
    handleMouseMove,
    handleMouseUp,
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    isEditingTextGlobal,
  ]);

  // Don't render overlay if not annotating or using image tool
  const showOverlay = isAnnotating && activeAnnotationType && activeAnnotationType !== 'image';

  // Render preview based on annotation type
  const renderPreview = () => {
    if (!isDrawing || !startPoint || !currentPoint) return null;

    switch (activeAnnotationType) {
      case 'line':
        return (
          <line
            x1={startPoint.x}
            y1={startPoint.y}
            x2={currentPoint.x}
            y2={currentPoint.y}
            stroke={currentStyle.color}
            strokeWidth={currentStyle.strokeWidth}
            strokeOpacity={currentStyle.opacity}
            strokeLinecap="round"
          />
        );

      case 'rectangle': {
        const width = Math.abs(currentPoint.x - startPoint.x);
        const height = Math.abs(currentPoint.y - startPoint.y);
        const x = Math.min(startPoint.x, currentPoint.x);
        const y = Math.min(startPoint.y, currentPoint.y);

        return (
          <rect
            x={x}
            y={y}
            width={width}
            height={height}
            fill="none"
            stroke={currentStyle.color}
            strokeWidth={currentStyle.strokeWidth}
            strokeOpacity={currentStyle.opacity}
          />
        );
      }

      case 'circle': {
        // Calculate circle with fixed starting point
        const dx = currentPoint.x - startPoint.x;
        const dy = currentPoint.y - startPoint.y;
        // Use the smaller dimension to keep it circular
        const diameter = Math.min(Math.abs(dx), Math.abs(dy));
        // Determine direction and calculate center
        const cx = startPoint.x + (dx > 0 ? diameter / 2 : -diameter / 2);
        const cy = startPoint.y + (dy > 0 ? diameter / 2 : -diameter / 2);

        return (
          <circle
            cx={cx}
            cy={cy}
            r={diameter / 2}
            fill="none"
            stroke={currentStyle.color}
            strokeWidth={currentStyle.strokeWidth}
            strokeOpacity={currentStyle.opacity}
          />
        );
      }

      case 'arrow': {
        const arrowDx = currentPoint.x - startPoint.x;
        const arrowDy = currentPoint.y - startPoint.y;
        const angle = Math.atan2(arrowDy, arrowDx);
        const arrowHeadLength = 15;
        const arrowHeadAngle = Math.PI / 6;

        const arrowPoint1 = {
          x: currentPoint.x - arrowHeadLength * Math.cos(angle - arrowHeadAngle),
          y: currentPoint.y - arrowHeadLength * Math.sin(angle - arrowHeadAngle),
        };

        const arrowPoint2 = {
          x: currentPoint.x - arrowHeadLength * Math.cos(angle + arrowHeadAngle),
          y: currentPoint.y - arrowHeadLength * Math.sin(angle + arrowHeadAngle),
        };

        return (
          <g>
            <line
              x1={startPoint.x}
              y1={startPoint.y}
              x2={currentPoint.x}
              y2={currentPoint.y}
              stroke={currentStyle.color}
              strokeWidth={currentStyle.strokeWidth}
              strokeOpacity={currentStyle.opacity}
              strokeLinecap="round"
            />
            <path
              d={`M ${currentPoint.x} ${currentPoint.y} L ${arrowPoint1.x} ${arrowPoint1.y} M ${currentPoint.x} ${currentPoint.y} L ${arrowPoint2.x} ${arrowPoint2.y}`}
              stroke={currentStyle.color}
              strokeWidth={currentStyle.strokeWidth}
              strokeOpacity={currentStyle.opacity}
              strokeLinecap="round"
              strokeLinejoin="round"
              fill="none"
            />
          </g>
        );
      }

      case 'text': {
        // Show preview rectangle for text area
        const textWidth = Math.abs(currentPoint.x - startPoint.x);
        const textHeight = Math.abs(currentPoint.y - startPoint.y);
        const textX = Math.min(startPoint.x, currentPoint.x);
        const textY = Math.min(startPoint.y, currentPoint.y);

        return (
          <rect
            x={textX}
            y={textY}
            width={Math.max(textWidth, 100)}
            height={Math.max(textHeight, 50)}
            fill="rgba(37, 99, 235, 0.1)"
            stroke={currentStyle.color}
            strokeWidth={currentStyle.strokeWidth}
            strokeOpacity={currentStyle.opacity}
            strokeDasharray="4 2"
          />
        );
      }

      default:
        return null;
    }
  };

  // Get annotation type label for tooltip
  const getAnnotationTypeLabel = () => {
    switch (activeAnnotationType) {
      case 'line':
        return 'Click and drag to draw a line (⇧ Shift for straight)';
      case 'rectangle':
        return 'Click and drag to draw a rectangle';
      case 'circle':
        return 'Click and drag to draw a circle';
      case 'arrow':
        return 'Click and drag to draw an arrow (⇧ Shift for straight)';
      case 'text':
        return 'Click and drag to create a text area';
      case 'image':
        return 'Click to place an image';
      default:
        return '';
    }
  };

  return (
    <>
      {/* Overlay for capturing clicks */}
      {showOverlay && (
        <div
          className="absolute inset-0 z-30"
          style={{
            cursor: 'crosshair',
            pointerEvents: 'auto',
            touchAction: 'none', // Prevent default touch behaviors like scrolling
          }}
        />
      )}

      {/* SVG for drawing preview - needs to be transformed to match viewport */}
      {showOverlay && (
        <div
          className="absolute inset-0 pointer-events-none"
          style={{
            transform: `translate(${position.x}px, ${position.y}px) scale(${zoom})`,
            transformOrigin: '0 0',
            zIndex: 50,
          }}
        >
          <svg
            className="pointer-events-none overflow-visible"
            style={{
              position: 'absolute',
              left: 0,
              top: 0,
              width: imageSize.width || 1920,
              height: imageSize.height || 1080,
            }}
            viewBox={`0 0 ${imageSize.width || 1920} ${imageSize.height || 1080}`}
          >
            {isDrawing && renderPreview()}
          </svg>
        </div>
      )}

      {/* Instructions tooltip */}
      {showOverlay && (
        <div
          className="absolute bottom-[env(safe-area-inset-bottom,16px)] left-1/2 -translate-x-1/2 bg-primary text-primary-foreground px-4 py-2 rounded shadow-lg pointer-events-none z-50"
          style={{ marginBottom: 'max(16px, env(safe-area-inset-bottom))' }}
        >
          <p className="text-sm font-medium">{getAnnotationTypeLabel()}</p>
          <p className="text-xs opacity-90">Press ESC to cancel</p>
        </div>
      )}
    </>
  );
}
